import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { supabase } from '../../supabase'
import { logError } from '../../util/errorLogger'

class PushNotificationService {
  private listenersAdded = false
  /** Last token received from the native registration listener; used to save once user is available */
  private lastReceivedToken: string | null = null

  async initialize() {
    // Skip if the platform is web or Android (to avoid Firebase initialization error)
    if (Capacitor.getPlatform() === 'web' || Capacitor.getPlatform() === 'android') return

    // Only add listeners once to avoid duplicates
    if (this.listenersAdded) {
      console.log('Push notification listeners already added, skipping')
      return
    }

    // Request permission to use push notifications
    // iOS will prompt user and return if they granted permission or not
    // Android will just grant without prompting
    const result = await PushNotifications.requestPermissions()
    if (result.receive === 'granted') {
      // Register with Apple / Google to receive push via APNS/FCM
      await PushNotifications.register()
    }

    // Handle initial registration and token updates
    // This fires when token is first registered OR when it changes
    PushNotifications.addListener('registration', async (token) => {
      console.log('Push registration success, token:', token.value)
      this.lastReceivedToken = token.value
      await this.saveDeviceToken(token.value)
    })

    // Handle token refresh/update (iOS/Android can refresh tokens)
    // This is critical - tokens can change and we need to update them
    PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
      console.log('Push notification action performed:', notification)
      // Re-register to ensure we have the latest token
      const result = await PushNotifications.requestPermissions()
      if (result.receive === 'granted') {
        await PushNotifications.register()
      }
      this.handleNotificationAction(notification)
    })

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error: any) => {
      void logError(error, { service: 'PushNotificationService', action: 'registration' })
    })

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification)
    })

    this.listenersAdded = true
  }

  private async saveDeviceToken(token: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        console.warn('No authenticated user, cannot save device token')
        return
      }

      // Upsert device token (user_devices: user_id, device_token; no updated_at)
      const { error } = await supabase.from('user_devices').upsert({
        user_id: user.id,
        device_token: token,
      }, {
        onConflict: 'user_id,device_token',
      })

      if (error) {
        void logError(error, { service: 'PushNotificationService', action: 'storeDeviceToken', userId: user.id })
        if (error.code === '23505' || error.message?.includes('duplicate')) {
          const { error: updateError } = await supabase
            .from('user_devices')
            .update({ device_token: token })
            .eq('user_id', user.id)
            .eq('device_token', token)

          if (updateError) {
            void logError(updateError, { service: 'PushNotificationService', action: 'updateDeviceToken', userId: user.id })
          } else {
            console.log('Device token updated successfully')
          }
        }
      } else {
        console.log('Device token saved successfully')
      }
    } catch (error: any) {
      void logError(error, { service: 'PushNotificationService', action: 'saveDeviceToken' })
    }
  }

  // Re-register tokens periodically or when app comes to foreground
  async refreshToken() {
    if (Capacitor.getPlatform() === 'web' || Capacitor.getPlatform() === 'android') return

    try {
      const result = await PushNotifications.requestPermissions()
      if (result.receive === 'granted') {
        await PushNotifications.register()
        console.log('Token refresh requested')
      }
      // Registration listener may not fire again if token is unchanged (known iOS behavior).
      // If we already have a token from a previous registration, save it now that user may be available.
      await this.ensureDeviceTokenSaved()
    } catch (error: any) {
      void logError(error, { service: 'PushNotificationService', action: 'refreshToken' })
    }
  }

  /** Save the last received token if we have one; used when user becomes available after initial registration. */
  async ensureDeviceTokenSaved() {
    if (this.lastReceivedToken) {
      await this.saveDeviceToken(this.lastReceivedToken)
    }
  }

  private handleNotificationAction(notification: any) {
    // Add logic to handle different notification types
    // For example, navigate to specific screens based on notification data
    const data = notification.notification.data

    switch (data.type) {
      case 'TEAM_INVITE':
        // Navigate to team invite screen
        break
      case 'ACTIVITY_LOGGED':
        // Navigate to activity details
        break
      case 'TEAM_CHAT':
        // Navigate to chat
        break
      // Add more cases as needed
    }
  }

  async getDeliveredNotifications() {
    const notificationList = await PushNotifications.getDeliveredNotifications()
    return notificationList.notifications
  }
}

export const pushNotificationService = new PushNotificationService()
